import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { StorageService, STORAGE_KEYS } from "../services/storage";
import { tabToRoute } from "../routes/routeConfig";
import { auth, onAuthStateChange } from "../utils/firebase";
import { loadAndSubscribeSystemSettings } from "../utils/commissionConfig";

import { AppTheme } from "../types";

const OFFICIAL_ADMIN_EMAILS = [
  "todossaodigitais@gmail.com",
  "admin@hubesportivo.com",
  "admin@suporte.com"
];

export type ActiveTabType =
  | "landing"
  | "hub"
  | "jogo"
  | "quadras"
  | "dayuse"
  | "mensalistas"
  | "financeiro"
  | "torneios"
  | "perfil"
  | "duplas"
  | "stats"
  | "historico"
  | "portal"
  | "admin"
  | "vendas"
  | "meus_agendamentos";

export type AdminSubTabType =
  | "quadras"
  | "fluxo_financeiro"
  | "bancos"
  | "torneios"
  | "configuracoes"
  | "bloqueios"
  | "aprovacao_arenas"
  | "denuncias_arenas";

export interface ToastMessage {
  type: "success" | "error";
  text: string;
}

export interface AppContextType {
  theme: AppTheme;
  setTheme: React.Dispatch<React.SetStateAction<AppTheme>>;
  zoomLevel: number;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetZoom: () => void;
  activeTab: ActiveTabType;
  setActiveTab: React.Dispatch<React.SetStateAction<ActiveTabType>>;
  handleSelectTab: (tab: any, pushHistory?: boolean) => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAdminMode: boolean;
  setIsAdminMode: React.Dispatch<React.SetStateAction<boolean>>;
  isSuperAdminAuth: boolean;
  setIsSuperAdminAuth: React.Dispatch<React.SetStateAction<boolean>>;
  adminSubTab: AdminSubTabType;
  setAdminSubTab: React.Dispatch<React.SetStateAction<AdminSubTabType>>;
  showDownloadAppModal: boolean;
  setShowDownloadAppModal: React.Dispatch<React.SetStateAction<boolean>>;
  toastMsg: ToastMessage | null;
  showToast: (text: string, type?: "success" | "error") => void;
  rachaHostId: string | null;
  setRachaHostId: React.Dispatch<React.SetStateAction<string | null>>;
  handleExitCoOrganizing: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Tema (Padrão: "light" conforme especificação da Etapa 17.2: Claro + Escuro)
  const [theme, setTheme] = useState<AppTheme>(() => {
    const saved = StorageService.getRaw(STORAGE_KEYS.THEME_MODE);
    if (saved === "light" || saved === "dark") {
      return saved as AppTheme;
    }
    // Caso o usuário possua preferência antiga "green" ou valor inválido, converter com segurança para "light"
    return "light";
  });

  useEffect(() => {
    StorageService.setRaw(STORAGE_KEYS.THEME_MODE, theme);
    const root = document.documentElement;
    const body = document.body;

    // Remove classes anteriores
    const themeClasses = ["light", "dark", "green", "theme-light", "theme-dark", "theme-green"];
    themeClasses.forEach((cls) => {
      root.classList.remove(cls);
      body.classList.remove(cls);
    });

    // Adiciona classes do tema ativo ("light" ou "dark")
    root.classList.add(theme, `theme-${theme}`);
    body.classList.add(theme, `theme-${theme}`);
    root.setAttribute("data-theme", theme);
    body.setAttribute("data-theme", theme);
  }, [theme]);

  // Sincronização global contínua de configurações do sistema (system_settings/global)
  useEffect(() => {
    const unsub = loadAndSubscribeSystemSettings();
    return () => {
      unsub();
    };
  }, []);

  // Zoom
  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const saved = StorageService.getRaw(STORAGE_KEYS.APP_ZOOM_LEVEL);
    return saved ? parseInt(saved, 10) : 75;
  });

  useEffect(() => {
    StorageService.setRaw(STORAGE_KEYS.APP_ZOOM_LEVEL, zoomLevel.toString());
  }, [zoomLevel]);

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(140, prev + 10));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(75, prev - 10));
  const handleResetZoom = () => setZoomLevel(100);

  // Navegação
  const [activeTab, setActiveTab] = useState<ActiveTabType>("hub");
  const [adminSubTab, setAdminSubTab] = useState<AdminSubTabType | "">("");

  const handleSelectTab = (tab: any, pushHistory: boolean = true) => {
    const targetTab: ActiveTabType = tab === "racha" ? "jogo" : tab;
    setActiveTab(targetTab);
    const targetPath = tabToRoute(targetTab, targetTab === "admin" ? (adminSubTab || undefined) : undefined);
    if (pushHistory && location.pathname !== targetPath) {
      navigate(targetPath + location.search);
    }
  };

  useEffect(() => {
    if (activeTab === "admin") {
      const targetPath = adminSubTab ? `/admin/${adminSubTab}` : "/admin";
      if (location.pathname !== targetPath) {
        navigate(targetPath + location.search);
      }
    }
  }, [adminSubTab, activeTab]);

  useEffect(() => {
    const path = location.pathname.toLowerCase().replace(/\/$/, "");
    if (path === "") {
      setActiveTab("landing");
    } else if (path === "/hub") {
      setActiveTab("hub");
    } else if (path === "/jogo" || path === "/racha") {
      setActiveTab("jogo");
    } else if (
      path === "/dayuse" ||
      path === "/arena/dayuse" ||
      path === "/horarios-eventos" ||
      path === "/arena/horarios-eventos"
    ) {
      setActiveTab("dayuse");
    } else if (
      path === "/mensalistas" ||
      path === "/arena/mensalistas" ||
      path === "/horario-fixo" ||
      path === "/arena/horario-fixo"
    ) {
      setActiveTab("mensalistas");
    } else if (path === "/financeiro" || path.startsWith("/financeiro") || path === "/arena/financeiro") {
      setActiveTab("financeiro");
    } else if (path === "/arena/torneios") {
      setActiveTab("torneios");
    } else if (path.startsWith("/arena/vendas") || path.startsWith("/arena/dashboard") || path === "/vendas") {
      setActiveTab("vendas");
    } else if (path === "/quadras" || path.startsWith("/arena/quadras") || path.startsWith("/arena/")) {
      setActiveTab("quadras");
    } else if (path === "/torneios" || path.startsWith("/torneio/")) {
      setActiveTab("torneios");
    } else if (path === "/perfil") {
      setActiveTab("perfil");
    } else if (path === "/duplas") {
      setActiveTab("duplas");
    } else if (path === "/stats") {
      setActiveTab("stats");
    } else if (path === "/historico") {
      setActiveTab("historico");
    } else if (path === "/portal" || path.startsWith("/inscricao")) {
      setActiveTab("portal");
    } else if (path === "/admin" || path.startsWith("/admin/")) {
      setActiveTab("admin");
    } else if (path === "/meus_agendamentos" || path === "/agendamentos") {
      setActiveTab("meus_agendamentos");
    }
  }, [location.pathname]);

  // Modais e UI
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDownloadAppModal, setShowDownloadAppModal] = useState(false);

  // Toast
  const [toastMsg, setToastMsg] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: "success" | "error" = "success") => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 3500);
  };

  // Super Admin
  const [isAdminMode, setIsAdminMode] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      const adminVal = p.get("admin") || p.get("superadmin");
      return Boolean(adminVal && adminVal !== "false" && adminVal !== "0");
    }
    return false;
  });

  const [isSuperAdminAuth, setIsSuperAdminAuth] = useState(() => {
    return sessionStorage.getItem("super_admin_auth_session_v1") === "true";
  });

  // Valida a autorização do Super Admin contra o Firebase Auth ativo
  useEffect(() => {
    const unsub = onAuthStateChange((user) => {
      const isSessionActive = sessionStorage.getItem("super_admin_auth_session_v1") === "true";
      if (isSessionActive) {
        if (!user || !user.email || !OFFICIAL_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
          console.warn("[AppContext] Sessão de Super Admin revogada: Usuário do Firebase Auth não é um administrador autorizado.");
          sessionStorage.removeItem("super_admin_auth_session_v1");
          setIsSuperAdminAuth(false);
        }
      }
    });
    return () => unsub();
  }, []);

  // Co-organizador de racha
  const [rachaHostId, setRachaHostId] = useState<string | null>(null);

  const handleExitCoOrganizing = () => {
    setRachaHostId(null);
    const newUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    showToast("Voltando para o seu painel local.", "success");
  };

  // URL listener especial (portal público, link secreto admin, etc.)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split("/");
    const inscricaoIndex = pathParts.indexOf("inscricao");

    let isPortal = params.get("portal") === "true";
    let tourneyId = params.get("torneio") || "";
    let action = params.get("acao") || "inscricao";

    if (inscricaoIndex !== -1 && pathParts[inscricaoIndex + 1]) {
      isPortal = true;
      tourneyId = pathParts[inscricaoIndex + 1];
      action = "inscricao";
    }

    if (isPortal) {
      setActiveTab("portal");
      if (tourneyId) {
        sessionStorage.setItem("public_portal_torneio_id", tourneyId);
        sessionStorage.setItem("public_portal_action", action);
      }
    }

    const hostParam = params.get("racha_host");
    if (hostParam) {
      setRachaHostId(hostParam);
      setActiveTab("jogo");
      showToast("Modo co-organizador ativado para este racha!", "success");
    }

    const tabParam = params.get("tab");
    if (tabParam === "admin") {
      setActiveTab("admin");
    }
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      zoomLevel,
      handleZoomIn,
      handleZoomOut,
      handleResetZoom,
      activeTab,
      setActiveTab,
      handleSelectTab,
      isSidebarOpen,
      setIsSidebarOpen,
      isAdminMode,
      setIsAdminMode,
      isSuperAdminAuth,
      setIsSuperAdminAuth,
      adminSubTab,
      setAdminSubTab,
      showDownloadAppModal,
      setShowDownloadAppModal,
      toastMsg,
      showToast,
      rachaHostId,
      setRachaHostId,
      handleExitCoOrganizing,
    }),
    [
      theme,
      zoomLevel,
      activeTab,
      isSidebarOpen,
      isAdminMode,
      isSuperAdminAuth,
      adminSubTab,
      showDownloadAppModal,
      toastMsg,
      rachaHostId,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp deve ser usado dentro de um AppProvider");
  }
  return context;
};
